<script>
  import ExternalLink from "./ExternalLink.svelte";
  import LinkButton from "./LinkButton.svelte";
  import { onMount, tick } from "svelte";

  import {
    CONTRACTS,
    ENDAOMENT_URL,
    GENERATIVE_FILM_URL,
    GITHUB_CODE_URL,
    HIGHLIGHT_URL,
    LINK_CREATE,
    LINK_EDITIONS,
    LINK_TERMS,
    MIN_ETH_PRICE_WITH_FEE_STRING,
    PATH_DONATE,
    SALE_END_DATE_FMT,
    SALE_START_DATE_FMT,
  } from "../util/constants";
  // import videoLoop from "../assets/video/punch-loop.mp4";
  // import flipLoop from "../assets/video/flip-loop.mp4";
  import Footer from "./Footer.svelte";
  import Anchor from "./Anchor.svelte";
  import EditionsZoomPreview from "./EditionsZoomPreview.svelte";
  import EtherscanLink from "./EtherscanLink.svelte";
  import Paragraph from "./Paragraph.svelte";
  import RoundLinkButton from "./RoundLinkButton.svelte";
  import { createAnchorScroller } from "../util/anchor-link";
  import Header from "./Header.svelte";
  // import cdcImage from "../assets/images/cdc.jpg";
  // import External from "../icons/external.svelte";
  import PunchLoopVideo from "./PunchLoopVideo.svelte";

  onMount(() => {
    const unsubAnchor = createAnchorScroller();
    return () => {
      unsubAnchor();
    };

    // const { hash } = document.location;
    // const scrollTo = hash && document.getElementById(hash.slice(1));
    // if (scrollTo) scrollTo.scrollIntoView();
  });
</script>

<section class="section main maxwidth">
  <div class="content">
    <Paragraph>
      Bitframes is a generative and systems-based artwork by <ExternalLink
        href="https://www.mattdesl.com">Matt DesLauriers</ExternalLink
      >, inspired by punched cards and early computer art. Design your own
      outputs with the <LinkButton upper to={`/${LINK_CREATE}`} inline
        >create</LinkButton
      > tool, and publish them to the blockchain for a small fee. 100% of the net
      proceeds are being used to fund the production of a feature-length documentary
      film on the history of generative art.
    </Paragraph>

    <div class="button-container">
      <RoundLinkButton href={`/${LINK_CREATE}`} text="CREATE A NEW DESIGN"
      ></RoundLinkButton>
    </div>

    <Anchor id="artwork" />
    <div class="header">encoded art</div>
    <Paragraph>
      From the 1960s to 1980s, punched cards were one of the primary means of
      interacting with a computer. A single paper card would encode 80 or so
      columns of data with specific hole-punches, and stacks of these would make
      a 'program'. Feeding this into the mainframe computer and waiting for a
      result is a task that might have taken hours or even days. Despite these
      constraints, artists of the era created remarkable works, demonstrating
      how constraint and limitation can sometimes act as a catalyst for
      creativity.
    </Paragraph>

    <PunchLoopVideo></PunchLoopVideo>
    <Paragraph>
      Bitframes celebrates this idea by using 32 bytes of data, which can neatly
      fit on a punched card, to construct each frame of a serial artwork.
      Changing even one bit, or a single hole punch, leads to an entirely
      different composition. Despite this constraint, the system is capable of
      millions of possibilities. Since each output can be encoded in a single
      256-bit integer, the entire collection of constructed encodings can be
      stored on the Ethereum blockchain, leveraging the ledger as a
      decentralized storage network.
    </Paragraph>

    <Anchor id="blockchain" />
    <div class="header">blockchain as an artistic medium</div>
    <Paragraph>
      Users of the system can publish or 'mint' their design to the Ethereum
      blockchain, which acts as a payment processor and distributed storage
      mechanism. Each mint is associated with a 32 byte encoding from which the
      artwork can be fully reconstructed. On the blockchain, each encoding is
      represented by a single integer, like a virtualized 'punched card'.
    </Paragraph>

    <div class="flip-loop-container">
      <video
        playsinline
        preload="auto"
        muted
        autoplay
        class="flip-loop noselect"
        loop
      >
        <source
          type="video/mp4"
          src="https://r2.bitframes.io/assets/video/flip-loop.mp4"
        />
      </video>
    </div>

    <Paragraph>
      The codec and rendering is open source and hackable; see <ExternalLink
        href={GITHUB_CODE_URL}>here</ExternalLink
      > for the source code.
    </Paragraph>

    <Anchor id="sale" />
    <div class="header">open edition sale</div>
    <Paragraph>
      The project is being released as a blockchain-based crowdfund via the <ExternalLink
        href="https://highlight.xyz">Highlight</ExternalLink
      >
      engine. To contribute, you will need an Ethereum wallet. Mint an output via
      the <LinkButton upper to={`/${LINK_CREATE}`} inline>create</LinkButton> tool.
      The minimum cost is
      <strong
        >{MIN_ETH_PRICE_WITH_FEE_STRING}
        ETH</strong
      >
      per transaction, but you can pay what you want above this. 100% of net proceeds
      (after crowdfund development expenses) are being directed to the production
      of <ExternalLink italic href={GENERATIVE_FILM_URL}
        >Generative</ExternalLink
      >, a feature-length documentary film about the history of generative art.
      By contributing, you'll receive a NFT of your design, and be entered into
      the
      <LinkButton to={`/${LINK_EDITIONS}`} inline>32 Bit Edition</LinkButton> raffle
      for that week.
    </Paragraph>
    <Paragraph>
      The crowdfund will run for four weeks from <strong
        >{SALE_START_DATE_FMT}</strong
      >
      to <strong>{SALE_END_DATE_FMT}</strong> (GMT). After this time, the edition
      size will be locked.
    </Paragraph>

    <Anchor id="film" />
    <div class="header">documentary film</div>
    <Paragraph>
      The film is being produced by Tordoff Films Ltd in the United Kingdom, and
      aims to be the definitive documentary covering the history of generative
      art from the 1950s until today. For more details, see <ExternalLink
        href={GENERATIVE_FILM_URL}>here</ExternalLink
      >.
      <div class="doc-media-container">
        <div class="doc-media-img-container noselect">
          <img
            loading="lazy"
            src="https://r2.bitframes.io/assets/images/cdc.jpg"
            alt="The CDC 6400 Computer System, a 1960s mainframe computer - source: NOIRLab"
          />
        </div>
        <div class="caption">
          <div>
            ↗ The CDC 6400, a mainframe computer that early artists like
            Manfred Mohr would have used in the late 1960s. Photo source: <ExternalLink
              href="https://commons.wikimedia.org/wiki/File:CDC_6400_Computer_System_(CDC-6400-Computer-System-CC).jpg"
              >NOIRLab</ExternalLink
            >.
          </div>
        </div>
      </div>
    </Paragraph>

    <Anchor id="edition" />
    <div class="header">32 bit edition</div>
    <Paragraph>
      <!-- In addition to the open edition crowdfund, there is a set of 32
      limited-edition tokens that will be distributed over four weeks. Of these,
      16 will be distributed via a weekly random raffle, and the remaining 16
      will be distributed to the project's top contributors once the crowdfund
      finishes. If your address has minted one or more tokens, it will be
      automatically entered into that week's raffle. -->
      Alongside the open edition crowdfund, there is a separate collection of 32
      limited-edition tokens, initially curated by the artist, that all users of
      the crowdfund will automatically be entered to win. These carry a unique palette
      and novel on-chain logic. See the <LinkButton to={`/${LINK_EDITIONS}`}
        >EDITIONS</LinkButton
      > page for more details.
    </Paragraph>
    <Paragraph></Paragraph>
  </div>
</section>

<div class="edition-preview noselect">
  <EditionsZoomPreview />
  <!-- <ArtworkPreview system={1} useGlobalState={false}></ArtworkPreview> -->
</div>
<section class="section maxwidth noborder">
  <div class="content faqs-content">
    <Anchor id="faq" />
    <!-- <div class="header first">FAQs</div> -->
    <Header>FAQs</Header>
    <div class="faqs-container">
      <div class="faqs">
        <div class="question">How do I contribute and mint?</div>
        <div class="answer">
          You will need an <ExternalLink href="https://ethereum.org/en/wallets/"
            >Ethereum wallet</ExternalLink
          >
          such as Metamask, and a minimum of
          <strong
            >{MIN_ETH_PRICE_WITH_FEE_STRING}
            ETH</strong
          >
          (however, you can contribute more than this if you want). 100% of net proceeds,
          after accounting for expenses related to the crowdfund development and
          processing, will be directed toward the film production.
        </div>
      </div>
      <div class="faqs">
        <div class="question">Can I donate any other way?</div>
        <div class="answer">
          Yes! We also accept donations through Endaoment and The Film
          Collaborative, both 501(c)(3) non-profit organizations, allowing for a
          range of payment options (debit, credit, Apple Pay) that are
          tax-deductible in the US. See the <LinkButton to={PATH_DONATE}
            >Donate</LinkButton
          > page for more details.
        </div>
      </div>
      <div class="faqs">
        <div class="question">How long is the sale open for?</div>
        <div class="answer">
          The sale opens {SALE_START_DATE_FMT}, and ends {SALE_END_DATE_FMT}.
        </div>
      </div>
      <div class="faqs">
        <div class="question">Where is the contract located?</div>
        <div class="answer">
          The main Bitframes contract is <EtherscanLink
            type="address"
            fragment={CONTRACTS.CROWDFUND.address}
          />, the open edition ERC-721 NFT contract is <EtherscanLink
            type="address"
            fragment={CONTRACTS.NFT_OPEN.address}
          />, and the 32 Bit Edition ERC-721 NFT contract is <EtherscanLink
            type="address"
            fragment={CONTRACTS.NFT_LIMITED.address}
          />.
        </div>
      </div>
      <div class="faqs">
        <div class="question">Where is the source code located?</div>
        <div class="answer">
          The source code for the codec and rendering system is available <ExternalLink
            href={GITHUB_CODE_URL}>here</ExternalLink
          > (MIT license).
        </div>
      </div>
      <div class="faqs">
        <Anchor id="edition-detail"></Anchor>
        <div class="question">How does the 32 Bit Edition work?</div>
        <div class="answer">
          <Paragraph>
            See the <LinkButton to={`/${LINK_EDITIONS}`}>EDITIONS</LinkButton> page
            for more details.
          </Paragraph>
        </div>
      </div>
      <div class="faqs">
        <div class="question">What does the NFT give me?</div>
        <div class="answer">
          The NFT acts as a proof of contribution to the crowdfund, and is also
          used as a mechanism for storing data without a centralized database.
          It does not give you any rights, ownership, or voting power over the
          film or its production.
        </div>
      </div>
      <div class="faqs">
        <div class="question">Can I print my design?</div>
        <div class="answer">
          Yes! The Bitframes codec and rendering system is fully open source
          (MIT license), so you can technically do anything with it. At a later
          point, the artist may choose to release some compositions as signed
          and limited edition prints, such as silk screen prints.
        </div>
      </div>
      <div class="faqs">
        <div class="question">What about the film?</div>
        <div class="answer">
          <em>Generative</em> is being produced independently by Tordoff Films Ltd.
          The film aims to be the definitive documentary on the history of generative
          art, from the 1950s until today. Matt has been supporting the film as a
          producer, lending advice and and some initial funding. A number of interviews
          with notable artists and curators have already been captured by the team.
          100% of the net proceeds (after expenses related to the crowdfund) from
          this sale will go towards film production, such as expenses related to
          interviewing key subjects around the world.
        </div>
      </div>
      <div class="faqs">
        <div class="question">Why crowdfund a film in this way?</div>
        <div class="answer">
          In line with the film's themes, a generative art and blockchain-first
          crowdfund mechanism seemed like an innovative and original approach to
          kickstarting a film production.
        </div>
      </div>
      <div class="faqs">
        <div class="question">What guarantees exist?</div>
        <div class="answer">
          <Paragraph>
            This is a donation-based crowdfund not subject to FCA regulation in
            the UK, and purchases are final and non-refundable. The artist has
            effectively chosen to donate his artwork to an independent film
            production company. The funds will at no point be under the artist's
            control; they will be managed by Tordoff Films Ltd. The artist,
            filmmakers and crowdfund platform makes no guarantees, and bears no
            responsibility for loss of user funds. In the case a film is not or
            cannot be made for whatever reason, contributors will not be
            refunded their donations.
          </Paragraph>
          <Paragraph>
            By minting a token, you are agreeing to the project's <LinkButton
              to={`/${LINK_TERMS}`}>Terms & Conditions</LinkButton
            >.
          </Paragraph>
        </div>
      </div>
    </div>
  </div>
</section>
<div class="hr"></div>
<section class="section noborder maxwidth">
  <div class="content acknowledgements">
    <Anchor id="acknowledgements" />
    <div class="header first">ACKNOWLEDGEMENTS</div>
    <Paragraph>
      The website and backend has been coded by the artist. Thanks to <ExternalLink
        href="https://alicepoma.com/">Alice Poma</ExternalLink
      > for her website design support, thanks to <ExternalLink
        href={HIGHLIGHT_URL}>Highlight</ExternalLink
      > for support with the Solidity contracts, and thanks to Tordoff Films Ltd
      for their support in producing the project.
    </Paragraph>
  </div>
</section>

<Footer></Footer>

<style>
  .section {
    width: 100%;
    display: flex;
    justify-content: space-between;
    box-sizing: border-box;
    padding: var(--main-gutter);
    padding-top: var(--main-gutter-vertical);
    padding-bottom: var(--main-gutter-vertical);
    flex-direction: column;
    /* border-bottom: 1px solid black; */
    font-size: 16px;
    border-bottom: 1px solid black;
  }
  .hr {
    width: 100%;
    border-bottom: 1px solid black;
  }
  .section.main {
    padding-bottom: 0;
    border-bottom: 0;
  }
  .content.acknowledgements,
  .content.faqs-content {
    padding-top: 20px;
    padding-bottom: 20px;
  }
  .content {
    max-width: 800px;
  }
  .button-container {
    margin-top: 40px;
    /* margin-bottom: 40px; */
    padding-left: 0;
    display: flex;
    justify-content: center;
  }
  .header {
    font-weight: bold;
    text-transform: uppercase;
    line-height: 1;
    margin-top: 40px;
    margin-bottom: var(--one-em);
    padding-bottom: 0;
  }
  .header.first {
    margin-top: 0px;
  }
  /* .section p:first-child {
    margin-top: 10px;
  } */

  /* p {
    line-height: 1.5;
    margin: 0;
    margin-top: 10px;
    margin-bottom: 10px;
  } */
  /* .section .answer p {
    margin-top: 0;
    padding: 0;
  } */
  .faqs-container {
    /* margin-top: 10px; */
  }
  .faqs-container .faqs {
    margin-bottom: calc(2 * var(--one-em));
  }

  .question {
    /* font-style: italic; */
    font-weight: bold;
    border-bottom: 1px dotted black;
    padding-bottom: 0px;
    /* margin-bottom: var(--one-em); */
    margin-bottom: 10px;
  }
  .faqs-container .faqs:last-child {
    margin-bottom: 0px;
  }
  .edition-preview {
    /* margin-top: calc(var(--main-gutter-vertical) - 10px); */
    margin-top: 20px;
    min-height: calc(max(350px, min(100vw, 70vh, 650px)));
    /* overflow: hidden; */
    /* width: calc(min(100%, 500px)); */
    width: 100%;
    box-sizing: border-box;
  }
  /* .row {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    box-sizing: border-box;
    flex-direction: row;
  }
  .brief {
    flex-shrink: 1;
    flex-grow: 1;
    max-width: 600px;
    min-width: 250px;
    flex-basis: 300px;
    font-size: 16px;
    box-sizing: border-box;
    padding-right: 40px;
  } */

  .flip-loop {
    user-select: none;
    -ms-user-select: none;
    -moz-user-select: none;
    -webkit-user-select: none;
    width: 100%;
    aspect-ratio: 1920/1080;
    height: auto;
  }
  .flip-loop-container {
    aspect-ratio: 1920/1080;
    background-color: black;
    /* max-width: 800px; */
  }
  .flip-loop-container {
    margin-top: 20px;
    margin-bottom: 20px;
    display: flex;
    will-change: transform;
    transform: translateZ(0);
    justify-content: center;
    align-items: center;
    overflow: hidden;
    width: 100%;
  }
  .noborder {
    border-bottom: 0;
  }
  .doc-media-container {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    max-width: 500px;
  }
  .caption {
    display: flex;
    justify-content: space-between;
    width: 100%;
    font-size: 10px;
  }
  .doc-media-img-container {
  }
  .doc-media-container img {
    background-color: black;
    aspect-ratio: 1625/1080;
    overflow: hidden;
    border-radius: 50px;
    width: 100%;
    height: auto;
    object-fit: cover;
  }
</style>
